import { IDocument } from '../models/document';

/**
 * Generic document source that can be adapted to IDocument
 * Allows using DocumentViewer with non-IDocument sources (invoices, external files, etc.)
 */
export interface GenericDocumentSource {
  /** Unique identifier */
  id: string | number;

  /** Display label */
  label: string;

  /** Function to get the view URL (for iframe/preview) */
  getViewUrl: () => Promise<string>;

  /** Optional function to get the download URL */
  getDownloadUrl?: () => Promise<string>;

  /** Optional metadata to store additional information */
  metadata?: Record<string, any>;

  /** Optional date */
  date?: Date;
}

/**
 * Extended IDocument interface with custom URL functions
 * Internal use only - allows DocumentViewer to work with custom sources
 */
export interface AdaptedIDocument extends IDocument {
  _getViewUrl?: () => Promise<string>;
  _getDownloadUrl?: () => Promise<string>;
  _metadata?: Record<string, any>;
}

/**
 * Converts a generic document source to an IDocument compatible with DocumentViewer
 *
 * @example
 * // For invoices
 * const invoiceDoc = adaptToIDocument({
 *   id: 'in_123abc',
 *   label: 'Facture du 15/12/2024',
 *   getViewUrl: async () => `/api/invoice-beneficiary/123/download/in_123abc?downloadType=view`,
 *   metadata: { amount: 154167, isPaid: true }
 * });
 *
 * @example
 * // For external documents
 * const externalDoc = adaptToIDocument({
 *   id: 'external-123',
 *   label: 'External PDF',
 *   getViewUrl: async () => 'https://example.com/document.pdf'
 * });
 */
export function adaptToIDocument(source: GenericDocumentSource): AdaptedIDocument {
  // Generate a numeric ID for compatibility
  const numericId = typeof source.id === 'number'
    ? source.id
    : parseInt(String(source.id).replace(/\D/g, ''), 10) || Date.now();

  return {
    id: numericId,
    label: source.label,
    path: '', // Not used for adapted documents
    downloadUrl: '', // Will be replaced by _getViewUrl/_getDownloadUrl
    dateCreation: source.date?.toISOString() || new Date().toISOString(),
    fileName: source.label,
    originalName: source.label,

    // Inject custom URL functions
    _getViewUrl: source.getViewUrl,
    _getDownloadUrl: source.getDownloadUrl,
    _metadata: source.metadata,

    // Spread metadata as properties for easy access in renderListItem
    ...(source.metadata || {}),
  } as AdaptedIDocument;
}

/**
 * Type guard to check if a document is an adapted document
 */
export function isAdaptedDocument(doc: IDocument): doc is AdaptedIDocument {
  return '_getViewUrl' in doc && typeof (doc as any)._getViewUrl === 'function';
}
